import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import {
  STANDARD_OBJECT_PRIMARY_ATTR_IDS,
  STANDARD_OBJECT_FALLBACK_ATTR_IDS,
} from "@/src/constants/crmConstants.js";
import store from "@/src/store/index.js";

export const propertiesHeaders = [
  { label: "Property Name", key: "name", width: 3 },
  { label: "Property Type", key: "type", width: 4 },
  { label: "Created By", key: "created_by", width: 3 },
  { label: "", key: "actions", width: 2 },
];

export const propertyTypeLabels = {
  text: "Text",
  number: "Number",
  date: "Date",
  single_select: "Single Select",
  user: "SuperAGI user",
};

export const entityConfigurations = {
  contact: {
    title: "Contact",
    icon: imageNames.CONTENT_SELECTED,
  },
  company: {
    title: "Company",
    icon: imageNames.COMPANIES_SELECTED,
  },
  deal: {
    title: "Deal",
    icon: imageNames.DEALS_SELECTED,
  },
  lead: {
    title: "Lead",
    icon: imageNames.LEADS_SELECTED,
  },
  customer: {
    title: "Customer",
    icon: imageNames.COMPANIES_SELECTED,
  },
};

export const getEntityConfiguration = (entityType, dynamicObjects = []) => {
  if (entityConfigurations[entityType]) {
    return entityConfigurations[entityType];
  }

  const dynamicObject = dynamicObjects.find((obj) => obj.slug === entityType);
  if (dynamicObject) {
    return {
      title: dynamicObject.name || entityType,
      icon: dynamicObject.icon
        ? imageNames[`${dynamicObject.icon.toUpperCase()}_SELECTED`] ||
          imageNames.CUSTOMERS_SELECTED
        : imageNames.CUSTOMERS_SELECTED,
    };
  }

  return {
    title: entityType.charAt(0).toUpperCase() + entityType.slice(1),
    icon: imageNames.CUSTOMERS_SELECTED,
  };
};

export const generateSlugFromName = (name) => {
  if (!name) return "";

  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const generateNameFromSlug = (slug) => {
  if (!slug) return "";

  return slug
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getPrimaryAttributeSlug = async (
  objectSlug,
  projectId = null,
  service = null,
) => {
  try {
    if (!objectSlug) return "";

    // Check Vuex cache first
    const cachedSlug = store.getters["schema/getPrimaryAttributeSlugBySlug"](
      objectSlug,
      projectId,
      service,
    );
    if (cachedSlug) {
      return cachedSlug;
    }

    // Fetch schema via Vuex store (handles caching internally)
    const response = await store.dispatch("schema/fetchSchema", {
      slug: objectSlug,
      projectId,
      service,
    });

    if (response?.success && response?.schema?.attributes) {
      const attrs = response.schema.attributes;
      const primaryAttr = attrs.find((attr) => attr?.is_primary === true);
      if (primaryAttr?.slug) return primaryAttr.slug;

      // Fallback for standard objects: resolve slug from hardcoded primary attribute IDs
      const standardPrimaryAttrIds =
        STANDARD_OBJECT_PRIMARY_ATTR_IDS[objectSlug.toLowerCase()];
      if (standardPrimaryAttrIds) {
        const ids = Array.isArray(standardPrimaryAttrIds)
          ? standardPrimaryAttrIds
          : [standardPrimaryAttrIds];
        for (const attrId of ids) {
          const standardAttr = attrs.find((attr) => attr?.id === attrId);
          if (standardAttr?.slug) return standardAttr.slug;
        }
      }

      // Secondary fallback: try first_name for leads/contacts (which have separate first_name/last_name instead of composite "name")
      const fallbackAttrId =
        STANDARD_OBJECT_FALLBACK_ATTR_IDS[objectSlug.toLowerCase()];
      if (fallbackAttrId) {
        const fallbackAttr = attrs.find((attr) => attr?.id === fallbackAttrId);
        if (fallbackAttr?.slug) return fallbackAttr.slug;
      }
    }

    return "";
  } catch (error) {
    console.error(
      "Failed to fetch primary attribute slug for",
      objectSlug,
      error,
    );
    return "";
  }
};

export const getPrimaryAttribute = async (objectSlug, projectId = null) => {
  try {
    if (!objectSlug) return null;

    // Check Vuex cache first
    const cachedAttr = store.getters["schema/getPrimaryAttributeBySlug"](
      objectSlug,
      projectId,
    );
    if (cachedAttr) {
      return cachedAttr;
    }

    // Fetch schema via Vuex store (handles caching internally)
    const response = await store.dispatch("schema/fetchSchema", {
      slug: objectSlug,
      projectId,
    });

    if (response?.success && response?.schema?.attributes) {
      let primaryAttr = response.schema.attributes.find(
        (attr) => attr?.is_primary === true,
      );

      // Fallback for standard objects: resolve from hardcoded primary attribute IDs
      if (!primaryAttr) {
        const standardPrimaryAttrIds =
          STANDARD_OBJECT_PRIMARY_ATTR_IDS[objectSlug.toLowerCase()];
        if (standardPrimaryAttrIds) {
          const ids = Array.isArray(standardPrimaryAttrIds)
            ? standardPrimaryAttrIds
            : [standardPrimaryAttrIds];
          for (const attrId of ids) {
            primaryAttr = response.schema.attributes.find(
              (attr) => attr?.id === attrId,
            );
            if (primaryAttr) break;
          }
        }
      }

      if (!primaryAttr) return null;
      return {
        slug: primaryAttr.slug || "",
        name: primaryAttr.name || "",
      };
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch primary attribute for", objectSlug, error);
    return null;
  }
};

// When the primary attribute has no value and the object uses sequential IDs
// with a display prefix, fall back to "<PREFIX>-<id>" (e.g. "PRD-6") rather
// than the raw numeric/UUID id so pickers and association cards stay readable.
const formatSequentialId = (id, { displayPrefix, idType } = {}) => {
  if (!id) return "";
  if (idType === "sequential" && displayPrefix) {
    return `${displayPrefix}-${id}`;
  }
  return String(id);
};

export const getDisplayValueFromPrimaryAttribute = (
  attributes,
  primaryAttrSlug,
  id,
  schemaObject = null,
) => {
  const sequential = () =>
    formatSequentialId(id, {
      displayPrefix: schemaObject?.display_prefix,
      idType: schemaObject?.id_type,
    });

  if (!primaryAttrSlug || !attributes?.[primaryAttrSlug]) {
    return sequential();
  }

  const attrVal = attributes[primaryAttrSlug];

  let value;
  if (attrVal && typeof attrVal === "object" && "value" in attrVal) {
    value = attrVal.value;
  } else {
    value = attrVal;
  }

  if (Array.isArray(value)) {
    const joined = value.join(", ").trim();
    return joined || sequential();
  }

  // Primary wins only when it has a real value — an empty string / null /
  // undefined means "not set", in which case fall back to sequential (PRD-6).
  const asString = value == null ? "" : String(value).trim();
  return asString || sequential();
};

const extractAttrValue = (attrValue) => {
  if (attrValue && typeof attrValue === "object" && "value" in attrValue) {
    return attrValue.value;
  }
  return attrValue;
};

export const getEntityDisplayName = (entity, primaryAttrSlug) => {
  if (!entity) return "";
  const attributes = entity.attributes || {};

  // 1. Use primary attribute slug from schema (handles renamed fields)
  if (primaryAttrSlug && attributes[primaryAttrSlug]) {
    const val = extractAttrValue(attributes[primaryAttrSlug]);

    if (val && typeof val === "object" && val.full_name) return val.full_name;
    if (val && typeof val === "object" && (val.first_name || val.last_name)) {
      return `${val.first_name || ""} ${val.last_name || ""}`.trim();
    }
    if (val && typeof val === "string" && val.trim()) return val;
    if (val != null && val !== "" && typeof val !== "object")
      return String(val);
  } else if (
    primaryAttrSlug &&
    attributes.name &&
    typeof attributes.name === "object"
  ) {
    // Primary attr slug (e.g. "first_name") is nested under attributes.name
    const nameObj = extractAttrValue(attributes.name);
    if (nameObj && typeof nameObj === "object") {
      if (nameObj.full_name) return nameObj.full_name;
      if (nameObj[primaryAttrSlug]) return String(nameObj[primaryAttrSlug]);
      if (nameObj.first_name || nameObj.last_name) {
        return `${nameObj.first_name || ""} ${nameObj.last_name || ""}`.trim();
      }
    }
  }

  // 2. Check flat entity property directly (e.g. deals have entity.title)
  if (primaryAttrSlug && entity[primaryAttrSlug]) {
    const val = entity[primaryAttrSlug];
    if (typeof val === "string" && val.trim()) return val;
    if (val != null && val !== "" && typeof val !== "object")
      return String(val);
  }

  // 3. Standard flat fields (backward compatibility)
  if (entity.first_name || entity.last_name) {
    return `${entity.first_name || ""} ${entity.last_name || ""}`.trim();
  }
  if (entity.name) return entity.name;

  return "";
};
